import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Music, Calendar, Mail, Phone, Clock, User, MessageSquare, LogOut, 
  Trash2, Check, X, MoreVertical, AlertCircle, Shield, Download, 
  Activity, RefreshCw, Quote
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLogin from './AdminLogin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth header
const createAuthAxios = (token) => {
  return axios.create({
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
};

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [securitySummary, setSecuritySummary] = useState(null);
  const [backupStats, setBackupStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null });

  useEffect(() => {
    // Check if user has a valid token
    const storedToken = localStorage.getItem('admin_token');
    if (storedToken) {
      verifyToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      await axios.get(`${API}/auth/verify`, {
        headers: { 'Authorization': `Bearer ${tokenToVerify}` }
      });
      setToken(tokenToVerify);
      setIsAuthenticated(true);
      fetchData(tokenToVerify);
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('admin_token');
      setLoading(false);
    }
  };

  const handleLogin = (newToken) => {
    setToken(newToken);
    setIsAuthenticated(true);
    fetchData(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const fetchData = async (authToken) => {
    setLoading(true);
    const authAxios = createAuthAxios(authToken);
    
    try {
      const [bookingsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/contact`)
      ]);
      setBookings(bookingsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSecurityData = async () => {
    if (!token) return;
    
    const authAxios = createAuthAxios(token);
    
    try {
      const [logsRes, summaryRes] = await Promise.all([
        authAxios.get(`${API}/admin/security/logs?limit=50`),
        authAxios.get(`${API}/admin/security/summary`)
      ]);
      setSecurityLogs(logsRes.data);
      setSecuritySummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching security data:', error);
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    }
  };

  const fetchBackupStats = async () => {
    if (!token) return;
    
    const authAxios = createAuthAxios(token);
    
    try {
      const statsRes = await authAxios.get(`${API}/admin/backup/stats`);
      setBackupStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching backup stats:', error);
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    }
  };

  const handleExportBackup = async () => {
    if (!token) return;
    
    try {
      const authAxios = createAuthAxios(token);
      const response = await authAxios.get(`${API}/admin/backup/export`);
      
      // Create and download file
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `guitar101_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully!');
    } catch (error) {
      console.error('Error exporting backup:', error);
      toast.error('Failed to export backup');
    }
  };

  // Load security/backup data when tab changes
  useEffect(() => {
    if (activeTab === 'security' && token) {
      fetchSecurityData();
    } else if (activeTab === 'backup' && token) {
      fetchBackupStats();
    }
  }, [activeTab, token]);

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}?status=${status}`);
      setBookings(bookings.map(b => b.id === id ? { ...b, status } : b));
      toast.success(`Booking marked as ${status}`);
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update booking');
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/${id}?status=${status}`);
      setContacts(contacts.map(c => c.id === id ? { ...c, status } : c));
      toast.success(`Contact marked as ${status}`);
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    }
  };

  const handleDelete = async () => {
    const { type, id } = deleteDialog;
    try {
      if (type === 'booking') {
        await axios.delete(`${API}/bookings/${id}`);
        setBookings(bookings.filter(b => b.id !== id));
        toast.success('Booking deleted');
      } else {
        await axios.delete(`${API}/contact/${id}`);
        setContacts(contacts.filter(c => c.id !== id));
        toast.success('Contact deleted');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    }
    setDeleteDialog({ open: false, type: null, id: null });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'new': return 'bg-purple-100 text-purple-800';
      case 'read': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEventTypeColor = (eventType) => {
    switch (eventType) {
      case 'login_success': return 'bg-green-100 text-green-800';
      case 'login_failed': return 'bg-red-100 text-red-800';
      case 'security_alert': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Show login page if not authenticated
  if (!isAuthenticated && !loading) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-100">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Music className="w-8 h-8 text-amber-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Guitar 101 Tuition</h1>
              <p className="text-sm text-gray-600">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => window.location.href = '/'}
              variant="outline" 
              className="border-amber-600 text-amber-600 hover:bg-amber-50"
            >
              Back to Website
            </Button>
            <Button 
              onClick={handleLogout}
              variant="outline" 
              className="border-red-600 text-red-600 hover:bg-red-50"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </nav>
      </header>

      {/* Admin Tools Navigation */}
      <div className="bg-white border-b border-amber-100 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm font-medium text-gray-500 mr-2">Quick Links:</span>
            <Button 
              onClick={() => setActiveTab('bookings')}
              variant={activeTab === 'bookings' ? 'default' : 'outline'}
              size="sm"
              className={activeTab === 'bookings' ? 'bg-amber-600 hover:bg-amber-700' : 'border-gray-300 hover:bg-gray-50'}
              data-testid="nav-bookings"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Bookings
            </Button>
            <Button 
              onClick={() => setActiveTab('contacts')}
              variant={activeTab === 'contacts' ? 'default' : 'outline'}
              size="sm"
              className={activeTab === 'contacts' ? 'bg-amber-600 hover:bg-amber-700' : 'border-gray-300 hover:bg-gray-50'}
              data-testid="nav-contacts"
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              Contacts
            </Button>
            <Button 
              onClick={() => setActiveTab('security')}
              variant={activeTab === 'security' ? 'default' : 'outline'}
              size="sm"
              className={activeTab === 'security' ? 'bg-green-600 hover:bg-green-700' : 'border-gray-300 hover:bg-gray-50'}
              data-testid="nav-security"
            >
              <Shield className="w-4 h-4 mr-1" />
              Security Logs
            </Button>
            <Button 
              onClick={() => setActiveTab('backup')}
              variant={activeTab === 'backup' ? 'default' : 'outline'}
              size="sm"
              className={activeTab === 'backup' ? 'bg-blue-600 hover:bg-blue-700' : 'border-gray-300 hover:bg-gray-50'}
              data-testid="nav-backup"
            >
              <Download className="w-4 h-4 mr-1" />
              Backup
            </Button>
            <div className="h-6 w-px bg-gray-300 mx-2"></div>
            <Button 
              onClick={() => window.location.href = '/admin/availability'}
              variant="outline"
              size="sm"
              className="border-purple-400 text-purple-600 hover:bg-purple-50"
              data-testid="nav-availability"
            >
              <Clock className="w-4 h-4 mr-1" />
              Manage Time Slots
            </Button>
            <Button 
              onClick={() => window.location.href = '/admin/testimonials'}
              variant="outline"
              size="sm"
              className="border-pink-400 text-pink-600 hover:bg-pink-50"
              data-testid="nav-testimonials"
            >
              <Quote className="w-4 h-4 mr-1" />
              Manage Testimonials
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h2>
          <p className="text-gray-600">Manage your trial bookings, contact inquiries, security, and backups</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="border-amber-100">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-600" />
                Trial Bookings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-gray-900">{bookings.length}</div>
              <p className="text-sm text-gray-600 mt-1">Total bookings</p>
            </CardContent>
          </Card>

          <Card className="border-amber-100">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-amber-600" />
                Contact Inquiries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-gray-900">{contacts.length}</div>
              <p className="text-sm text-gray-600 mt-1">Total inquiries</p>
            </CardContent>
          </Card>

          <Card className="border-green-100">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                Security Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-green-600">Protected</div>
              <p className="text-sm text-gray-600 mt-1">JWT Authentication</p>
            </CardContent>
          </Card>

          <Card className="border-blue-100">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="w-5 h-5 text-blue-600" />
                Backup
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleExportBackup}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="quick-backup-button"
              >
                Quick Export
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-2xl grid-cols-4">
            <TabsTrigger value="bookings" data-testid="bookings-tab">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="contacts-tab">Contacts ({contacts.length})</TabsTrigger>
            <TabsTrigger value="security" data-testid="security-tab">Security</TabsTrigger>
            <TabsTrigger value="backup" data-testid="backup-tab">Backup</TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            {bookings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No trial bookings yet</p>
                </CardContent>
              </Card>
            ) : (
              bookings.map((booking) => (
                <Card key={booking.id} className="border-amber-100 hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-xl flex items-center gap-2">
                          <User className="w-5 h-5 text-amber-600" />
                          {booking.name}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Submitted: {formatDate(booking.createdAt)}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(booking.status)}>
                          {booking.status}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => updateBookingStatus(booking.id, 'confirmed')}>
                              <Check className="w-4 h-4 mr-2" />
                              Mark Confirmed
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateBookingStatus(booking.id, 'completed')}>
                              <Check className="w-4 h-4 mr-2" />
                              Mark Completed
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateBookingStatus(booking.id, 'cancelled')}>
                              <X className="w-4 h-4 mr-2" />
                              Mark Cancelled
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeleteDialog({ open: true, type: 'booking', id: booking.id })}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-gray-700">
                          <Mail className="w-4 h-4 text-amber-600" />
                          <span className="text-sm">{booking.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <Phone className="w-4 h-4 text-amber-600" />
                          <span className="text-sm">{booking.phone}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <Music className="w-4 h-4 text-amber-600" />
                          <span className="text-sm">{booking.instrument}</span>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-gray-700">
                          <Calendar className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-semibold">
                            {booking.preferredDay ? `Preferred Day: ${booking.preferredDay}` : `Preferred Date: ${booking.preferredDate}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <Clock className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-semibold">Preferred Time: {booking.preferredTime}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="space-y-4">
            {contacts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No contact inquiries yet</p>
                </CardContent>
              </Card>
            ) : (
              contacts.map((contact) => (
                <Card key={contact.id} className="border-amber-100 hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-xl flex items-center gap-2">
                          <User className="w-5 h-5 text-amber-600" />
                          {contact.name}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Submitted: {formatDate(contact.createdAt)}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(contact.status)}>
                          {contact.status}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => updateContactStatus(contact.id, 'read')}>
                              <Check className="w-4 h-4 mr-2" />
                              Mark as Read
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateContactStatus(contact.id, 'replied')}>
                              <Check className="w-4 h-4 mr-2" />
                              Mark as Replied
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeleteDialog({ open: true, type: 'contact', id: contact.id })}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-2 text-gray-700">
                          <Mail className="w-4 h-4 text-amber-600" />
                          <span className="text-sm">{contact.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <Phone className="w-4 h-4 text-amber-600" />
                          <span className="text-sm">{contact.phone}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <Music className="w-4 h-4 text-amber-600" />
                          <span className="text-sm">Interested in: {contact.instrument}</span>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-sm font-semibold text-gray-900 mb-2">Message:</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{contact.message}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Security Monitoring</h3>
              <Button 
                onClick={fetchSecurityData}
                variant="outline"
                size="sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {/* Security Summary */}
            {securitySummary && (
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <Card className="border-green-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-gray-600">Successful Logins (24h)</span>
                    </div>
                    <div className="text-3xl font-bold text-green-600 mt-2">
                      {securitySummary.last_24_hours?.successful_logins || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-red-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2">
                      <X className="w-5 h-5 text-red-600" />
                      <span className="text-sm text-gray-600">Failed Logins (24h)</span>
                    </div>
                    <div className="text-3xl font-bold text-red-600 mt-2">
                      {securitySummary.last_24_hours?.failed_logins || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-orange-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                      <span className="text-sm text-gray-600">Security Alerts (24h)</span>
                    </div>
                    <div className="text-3xl font-bold text-orange-600 mt-2">
                      {securitySummary.last_24_hours?.security_alerts || 0}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Security Logs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Recent Security Events
                </CardTitle>
                <CardDescription>Last 50 security events</CardDescription>
              </CardHeader>
              <CardContent>
                {securityLogs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No security events recorded yet</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {securityLogs.map((log, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge className={getEventTypeColor(log.event_type)}>
                            {log.event_type.replace('_', ' ')}
                          </Badge>
                          <span className="text-sm text-gray-600">{log.ip_address}</span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backup Tab */}
          <TabsContent value="backup" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Database Backup</h3>
              <Button 
                onClick={fetchBackupStats}
                variant="outline"
                size="sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Stats
              </Button>
            </div>

            {/* Backup Stats */}
            {backupStats && (
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-sm text-gray-600">Trial Bookings</div>
                    <div className="text-3xl font-bold text-amber-600 mt-2">
                      {backupStats.counts?.trial_bookings || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-sm text-gray-600">Contact Submissions</div>
                    <div className="text-3xl font-bold text-amber-600 mt-2">
                      {backupStats.counts?.contact_submissions || 0}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-sm text-gray-600">Time Slots</div>
                    <div className="text-3xl font-bold text-amber-600 mt-2">
                      {backupStats.counts?.time_slots || 0}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Export Options */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Export Data
                </CardTitle>
                <CardDescription>Download your data as JSON files for backup</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  onClick={handleExportBackup}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  data-testid="full-backup-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Full Backup (All Data)
                </Button>
                
                <div className="text-sm text-gray-500 text-center">
                  Backup includes: Bookings, Contacts, and Time Slot settings
                </div>
              </CardContent>
            </Card>

            {/* Backup Tips */}
            <Card className="bg-blue-50 border-blue-100">
              <CardContent className="pt-6">
                <h4 className="font-semibold text-blue-800 mb-2">Backup Tips</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Download backups regularly (weekly recommended)</li>
                  <li>• Store backup files in a secure location</li>
                  <li>• Keep multiple versions of backups</li>
                  <li>• Test your backups periodically</li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Confirm Deletion
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {deleteDialog.type === 'booking' ? 'booking' : 'contact inquiry'}? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Admin;
